/**
 * General-purpose array sorting utility
 *
 * Supports alphabetical, date-based, reverse, or no sorting.
 * Prevents mutation by copying the array internally.
 *
 * @param {Array} arr - The array to sort
 * @param {Object} options - Configurable options
 * @param {string} [options.key="name"] - The object key to sort by (e.g. "name", "created_at")
 * @param {"asc"|"desc"} [options.order="asc"] - Sort order
 * @param {"alpha"|"date"|"reverse"|"none"} [options.mode="alpha"] - Sort mode
 *
 * @returns {Array} - A new, sorted array
 *
 * ---------------------------
 * Use cases:
 *
 * // 1. Alphabetical A → Z by "name"
 * sortData(data, { mode: "alpha" })
 *
 * // 2. Alphabetical Z → A by "name"
 * sortData(data, { mode: "alpha", order: "desc" })
 *
 * // 3. Sort by "created_at" date (oldest → newest) // or add custom key eg, name, contact_type etc
 * sortData(data, { mode: "date", key: "created_at" })
 *
 * // 4. Sort by "created_at" date (newest first)
 * sortData(data, { mode: "date", key: "created_at", order: "desc" })
 *
 * // 5. Reverse array order (e.g. latest data first if API returns oldest first)
 * sortData(data, { mode: "reverse" })
 *
 * // 6. Leave data as-is (no sorting)
 * sortData(data, { mode: "none" })
 */
export const sortData = (arr, options = {}) => {
    const {
        key = "name",
        order = "asc",
        mode = "alpha",
    } = options;

    let sorted = arr.slice(); // prevent mutation

    if (mode === "alpha") {
        sorted.sort((a, b) => {
            const aVal = a[key] ?? "";
            const bVal = b[key] ?? "";
            return aVal.toString().localeCompare(bVal.toString());
        });
    }

    if (mode === "date") {
        sorted.sort((a, b) => new Date(a[key]) - new Date(b[key]));
    }

    if (mode === "reverse") {
        sorted.reverse();
    }

    if (order === "desc" && mode !== "reverse") {
        sorted.reverse();
    }

    return sorted;
};
